import React, { useEffect, useState } from "react";
import useSWR from "swr";
import CloseIcon from "@mui/icons-material/Close";

import { useNotification } from "../../Notifications/NotificationProvider";
import RejectIcon from "../icons/reject.svg";
import ApproveIcon from "../icons/approve.svg";
import "../styles/modalStyle.scss";
import DexterIcon from "../icons/dexter-pink.svg";
import BipononIcon from "../icons/biponon-pink.svg";
import LoanHistoryCard from "./LoanHistoryCard";
import LoanRequestDetailsCard from "./LoanRequestDetailsCard";
import MerchantDetailsCard from "./MerchantDetailsCard";
import QuarterSummaryCard from "./QuarterSummaryCard";
import VisitCallSummaryCard from "./VisitCallSummaryCard";
import VisitCallImages from "./VisitCallImages";
import RecentTransactionSummary from "./RecentTransactionSummary";
import LoanConfirmationModal from "./LoanConfirmationModal";
import { fetcher } from "../../../utilities/_fetcher";
import {
  getLastNBipononCallSummaryUrl,
  getLastNCallSummaryUrl,
  getLoanReviewUrl,
  getMerchantInformationUrl,
  getTransactionSummaryUrl,
} from "../service/loanService";

const LoanApprovalModal = ({ onClose, onActionComplete, loan }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const dispatchNotification = useNotification();
  const localUserType = JSON.parse(localStorage.getItem("currUserType"));
  const canApproveReject =
    localUserType === "agency_user" && loan?.request_status === "Pending @ OM";

  const { data: loanReviewResponse, error: loanReviewError } = useSWR(
    loan?.wallet_number && loan?.identity_id
      ? getLoanReviewUrl({
          walletNumber: loan.wallet_number,
          identityId: loan.identity_id,
          loanRequestId: loan.loan_request_id,
        })
      : null,
    fetcher,
    {
      fallbackData: {
        loan_info: {
          amount: null,
          installment_amount: null,
          installment_frequency: null,
          installment_count: null,
        },
        loan_history: {
          loans_taken: null,
          unrecovered_count: null,
          recovered_count: null,
          ongoing_loan_count: null,
          late_payment_percentage: null,
          // pre_approved_count: null,
        },
        need_to_retry: null,
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load loan review data";

        dispatchNotification({
          type: "ERROR",
          message: errorMessage,
        });
      },
      refreshInterval: 5 * 60 * 1000,
    }
  );

  const { data: merchantResponse, error: merchantError } = useSWR(
    loan?.wallet_number
      ? getMerchantInformationUrl({ walletNumber: loan.wallet_number })
      : null,
    fetcher,
    {
      fallbackData: {
        [loan?.wallet_number]: {
          wallet_number: null,
          merchant_name: null,
          shop_address: null,
          merchant_full_name: null,
          registration_date: null,
          product_type: null,
          bmcc_code: null,
          bmcc_name: null,
          assigned_mdo_name: null,
          assigned_mdo_number: null,
          assigned_mdo_id: null,
          assigned_mdo_image: null,
          assigned_mdo_age: null,
          merchant_age: null,
          quarter_summary: [],
        },
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load merchant data";

        dispatchNotification({
          type: "ERROR",
          message: errorMessage,
        });
      },
      refreshInterval: 5 * 60 * 1000,
    }
  );

  const { data: callsResponse, error: callsError } = useSWR(
    loan?.wallet_number
      ? getLastNCallSummaryUrl({ walletNumber: loan.wallet_number })
      : null,
    fetcher,
    {
      fallbackData: {
        [loan?.wallet_number]: {
          calls_summary: {
            open: null,
            close: null,
            not_found: null,
            total: null,
          },
          calls_data: [],
        },
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load visit call data";

        dispatchNotification({
          type: "ERROR",
          message: errorMessage,
        });
      },
      refreshInterval: 5 * 60 * 1000,
    }
  );

  const { data: bipononCallsResponse, error: bipononCallsError } = useSWR(
    loan?.wallet_number
      ? getLastNBipononCallSummaryUrl({ walletNumber: loan.wallet_number })
      : null,
    fetcher,
    {
      fallbackData: {
        [loan?.wallet_number]: {
          calls_summary: {
            open: null,
            close: null,
            not_found: null,
            total: null,
          },
          calls_data: [],
        },
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load Biponon visit call data";

        dispatchNotification({
          type: "ERROR",
          message: errorMessage,
        });
      },
      refreshInterval: 5 * 60 * 1000,
    }
  );

  const { data: transactionResponse, error: transactionError } = useSWR(
    loan?.wallet_number
      ? getTransactionSummaryUrl({ walletNumber: loan.wallet_number })
      : null,
    fetcher,
    {
      fallbackData: {
        [loan?.wallet_number]: [],
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load transaction data";

        dispatchNotification({
          type: "ERROR",
          message: errorMessage,
        });
      },
      refreshInterval: 5 * 60 * 1000,
    }
  );

  const loanReviewLoading = !loanReviewResponse && !loanReviewError;
  const merchantLoading = !merchantResponse && !merchantError;
  const callsLoading = !callsResponse && !callsError;
  const bipononCallsLoading = !bipononCallsResponse && !bipononCallsError;
  const transactionLoading = !transactionResponse && !transactionError;
  const canRetry = loanReviewResponse?.need_to_retry === true;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!canApproveReject || canRetry) return;

      if (e.key === "Control" && e.ctrlKey) {
        e.preventDefault();
        handleActionClick("reject");
      }
      if (e.code === "Space") {
        e.preventDefault();
        handleActionClick("recommend");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canApproveReject, canRetry]);

  const handleActionClick = (action) => {
    setConfirmationAction(action);
    setShowConfirmation(true);
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const handleConfirmationSubmit = (action, reasons) => {
    setShowConfirmation(false);
    setConfirmationAction(null);
    if (onActionComplete) {
      onActionComplete(action, reasons);
    }
    onClose();
  };

  return (
    <>
      <div className="approval-modal-overlay">
        <div className="approval-modal">
          <div className="approval-modal__header">
            <h2 className="approval-modal__title">
              Approve/reject loan for{" "}
              {loan?.merchant_name || "Unknown Merchant"}
            </h2>
            <button className="approval-modal__close" onClick={onClose}>
              <CloseIcon size={20} />
            </button>
          </div>

          <div className="approval-modal__content">
            <div className="loan-content-grid">
              <div className="loan-info-container">
                <div className="merchant-details-container">
                  <div className="loan-details">
                    <LoanRequestDetailsCard
                      {...(loanReviewResponse?.loan_info || {})}
                      loading={loanReviewLoading}
                      error={!!loanReviewError}
                      errorMessage={
                        loanReviewError?.message ||
                        "Unable to fetch loan review data"
                      }
                    />
                    <LoanHistoryCard
                      {...(loanReviewResponse?.loan_history || {})}
                      loading={loanReviewLoading}
                      error={!!loanReviewError}
                      errorMessage={
                        loanReviewError?.message ||
                        "Unable to fetch loan review data"
                      }
                    />
                  </div>
                  <div className="merchant-details">
                    <MerchantDetailsCard
                      {...(merchantResponse?.[loan?.wallet_number] || {})}
                      loading={merchantLoading}
                      error={!!merchantError}
                      errorMessage={
                        merchantError?.message ||
                        "Unable to fetch merchant data"
                      }
                    />
                  </div>
                  <div className="visit-details">
                    <QuarterSummaryCard
                      quarterSummary={
                        merchantResponse?.[loan?.wallet_number]
                          ?.quarter_summary || []
                      }
                      loading={merchantLoading}
                      error={!!merchantError}
                      errorMessage={
                        merchantError?.message ||
                        "Unable to fetch merchant data"
                      }
                    />
                    <VisitCallSummaryCard
                      title="Dexter Last 6 Visit Summary"
                      icon={DexterIcon}
                      {...(callsResponse?.[loan?.wallet_number]
                        ?.calls_summary || {})}
                      loading={callsLoading}
                      error={!!callsError}
                      errorMessage={
                        callsError?.message || "Unable to fetch calls data"
                      }
                    />
                    <VisitCallSummaryCard
                      title="Biponon Last 6 Visit Summary"
                      icon={BipononIcon}
                      {...(bipononCallsResponse?.[loan?.wallet_number]
                        ?.calls_summary || {})}
                      loading={bipononCallsLoading}
                      error={!!bipononCallsError}
                      errorMessage={
                        bipononCallsError?.message ||
                        "Unable to fetch Biponon calls data"
                      }
                    />
                  </div>
                </div>
                <RecentTransactionSummary
                  transactionData={
                    transactionResponse?.[loan?.wallet_number] || []
                  }
                  loading={transactionLoading}
                  error={!!transactionError}
                  errorMessage={
                    transactionError?.message ||
                    "Unable to fetch transaction data"
                  }
                />
              </div>

              <div className="merchant-images-container">
                <VisitCallImages
                  title="Dexter Visit Images"
                  icon={DexterIcon}
                  calls={callsResponse?.[loan?.wallet_number]?.calls_data || []}
                  loading={callsLoading}
                  error={!!callsError}
                  errorMessage={
                    callsError?.message || "Unable to fetch calls data"
                  }
                />
                <VisitCallImages
                  title="Biponon Visit Images"
                  icon={BipononIcon}
                  calls={
                    bipononCallsResponse?.[loan?.wallet_number]?.calls_data ||
                    []
                  }
                  loading={bipononCallsLoading}
                  error={!!bipononCallsError}
                  errorMessage={
                    bipononCallsError?.message ||
                    "Unable to fetch Biponon calls data"
                  }
                />
              </div>
            </div>
          </div>

          <div className="approval-modal__footer">
            <div className="keyboard-hint">
              <p className="keyboard-hint-text">
                {canApproveReject
                  ? canRetry
                    ? 'Press "Up" or "Down" on your keyboard to navigate leads.'
                    : 'Press "Up" or "Down" on your keyboard to navigate leads. Press "Space" to approve or "Ctrl" to reject leads.'
                  : 'Press "Up" or "Down" on your keyboard to navigate leads.'}
              </p>
            </div>
            {!loanReviewLoading && canApproveReject && (
              <div className="action-buttons">
                {canRetry ? (
                  <button
                    className="btn btn--recommend"
                    onClick={() => handleActionClick("retry")}
                  >
                    <img src={ApproveIcon} alt="Retry" className="btn--icon" />
                    Retry
                  </button>
                ) : (
                  <>
                    <button
                      className="btn btn--reject"
                      onClick={() => handleActionClick("reject")}
                    >
                      <img
                        src={RejectIcon}
                        alt="Reject"
                        className="btn--icon"
                      />
                      Reject
                    </button>
                    <button
                      className="btn btn--recommend"
                      onClick={() => handleActionClick("recommend")}
                    >
                      <img
                        src={ApproveIcon}
                        alt="Recommend"
                        className="btn--icon"
                      />
                      Recommend
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showConfirmation && (
        <LoanConfirmationModal
          loan={loan}
          action={confirmationAction}
          onClose={handleConfirmationClose}
          onConfirm={handleConfirmationSubmit}
        />
      )}
    </>
  );
};

export default LoanApprovalModal;
